import { _ } from '@splunk/ui-utils/i18n';
import { console as consoleClass } from '@splunk/dashboard-utils';
import { useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import type { RenderMetrics } from '@splunk/dashboard-types';

export interface UseMutationObserverPerformanceProps {
    containerRef: React.RefObject<HTMLElement>; // Required container ref
    stabilityThreshold?: number; // Time in ms of no mutations to consider rendering complete
    maxTimeout?: number; // Maximum time to wait before forcing completion
    onRenderComplete: (metrics: RenderMetrics) => void; // Callback when rendering is complete
    dependencies?: unknown[]; // Dependencies array to track for changes
    enabled?: boolean; // Whether the observer is enabled (default: true)
}

const emptyDeps: unknown[] = [];

export const useMutationObserverPerformance = ({
    containerRef,
    stabilityThreshold = 500,
    maxTimeout = 60000,
    onRenderComplete,
    dependencies = emptyDeps,
    enabled = true,
}: UseMutationObserverPerformanceProps) => {
    const previousDepsRef = useRef<unknown[]>();
    const observerRef = useRef<MutationObserver | null>(null);
    const onRenderCompleteRef = useRef(onRenderComplete);
    onRenderCompleteRef.current = onRenderComplete;

    const timersRef = useRef<{
        stabilityTimer: ReturnType<typeof setTimeout> | undefined;
        maxTimer: ReturnType<typeof setTimeout> | undefined;
        fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    }>({
        stabilityTimer: undefined,
        maxTimer: undefined,
        fallbackTimer: undefined,
    });

    // Track measurement state
    const measurementRef = useRef<{
        startTime: number;
        lastMutationTime: number;
        mutationCount: number;
        isComplete: boolean;
    }>({
        startTime: 0,
        lastMutationTime: 0,
        mutationCount: 0,
        isComplete: false,
    });

    // Check if dependencies changed (proper array comparison)
    const depsChanged =
        previousDepsRef.current === undefined ||
        previousDepsRef.current.length !== dependencies.length ||
        previousDepsRef.current.some(
            (dep, index) => !Object.is(dep, dependencies[index])
        );

    // Start timing immediately when dependencies change
    if (depsChanged || previousDepsRef.current === undefined) {
        const now = performance.now();
        measurementRef.current.startTime = now;
        measurementRef.current.lastMutationTime = 0;
        measurementRef.current.mutationCount = 0;
        measurementRef.current.isComplete = false;
        previousDepsRef.current = [...dependencies];
    }

    // Clean up all timers and observer
    const cleanup = useCallback(() => {
        const timers = timersRef.current;

        clearTimeout(timers.stabilityTimer);
        timers.stabilityTimer = undefined;

        clearTimeout(timers.maxTimer);
        timers.maxTimer = undefined;

        clearTimeout(timers.fallbackTimer);
        timers.fallbackTimer = undefined;

        // Disconnect observer
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
    }, []);

    // Complete the measurement with given reason
    const completeMeasurement = useCallback(
        (reason: RenderMetrics['completionReason']) => {
            const measurement = measurementRef.current;

            if (measurement.isComplete) {
                return; // Already completed
            }

            measurement.isComplete = true;
            cleanup();

            const endTime = performance.now();
            // If completion is due to timeout, the end time is the timeout itself.
            // Otherwise, it's the time of the last mutation.
            const finalEndTime =
                reason === 'timeout'
                    ? endTime
                    : measurement.lastMutationTime || endTime;
            const totalTime = finalEndTime - measurement.startTime;

            const metrics: RenderMetrics = {
                totalTime,
                mutationCount: measurement.mutationCount,
                startTime: measurement.startTime,
                endTime: finalEndTime,
                completionReason: reason,
            };

            try {
                onRenderCompleteRef.current(metrics);
            } catch (error) {
                consoleClass.error(
                    _('Error in onRenderComplete callback:'),
                    error
                );
            }
        },
        [cleanup]
    );

    // Main effect for setting up the mutation observer
    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!enabled || !container) {
            return undefined;
        }

        const measurement = measurementRef.current;

        // Create mutation observer
        observerRef.current = new MutationObserver((mutations) => {
            if (measurement.isComplete) {
                observerRef.current?.disconnect();
                return; // Ignore mutations after completion
            }

            const now = performance.now();
            measurement.mutationCount += mutations.length;
            measurement.lastMutationTime = now;

            // Clear existing stability timer
            clearTimeout(timersRef.current.stabilityTimer);

            // Set new stability timer
            timersRef.current.stabilityTimer = setTimeout(() => {
                completeMeasurement('stability');
            }, stabilityThreshold);
        });

        // Start observing with comprehensive options
        observerRef.current.observe(container, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
        });

        // Set maximum timeout to prevent infinite waiting
        timersRef.current.maxTimer = setTimeout(() => {
            completeMeasurement('timeout');
        }, maxTimeout);

        // Add fallback timer to handle case when no mutations occur
        timersRef.current.fallbackTimer = setTimeout(() => {
            // Only proceed if no mutations have occurred and measurement hasn't completed
            if (
                measurementRef.current.mutationCount === 0 &&
                !measurementRef.current.isComplete
            ) {
                completeMeasurement('no-mutations');
            }
        }, stabilityThreshold * 2);

        return () => {
            cleanup();
        };
    }, [
        enabled,
        containerRef,
        dependencies,
        stabilityThreshold,
        maxTimeout,
        completeMeasurement,
        cleanup,
    ]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);
};
